import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { MatIcon } from '@angular/material/icon';
import { ToastService } from '../services/toastr.service';

@Component({
  selector: 'lib-toast',
  standalone: true,
  imports: [AsyncPipe, MatIcon],
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20%)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-10%)' })),
      ]),
    ]),
  ],
  template: `
    <div class="toast-container position-fixed top-0 end-0 p-4">
      @for (toast of toasts$ | async; track toast.id) {
        <div class="premium-toast" [@toastAnimation] [class]="toast.class" role="alert">
            <div class="design-elements">
              <mat-icon class="bg-icon-1">{{toast.icon}}</mat-icon>
              <mat-icon class="bg-icon-2">{{toast.icon}}</mat-icon>
            </div>
            <div class="toast-main-content">
              <div class="icon-section">
                <mat-icon>{{toast.icon}}</mat-icon>
              </div>
              <div class="text-section">
                @if(toast.title){
                  <div class="toast-title">{{toast.title}}</div>
                }
                <div class="toast-message">{{ toast.message }}</div>
              </div>
              <div class="action-section">
                <button class="close-btn" (click)="toastService.dismiss(toast.id)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            <div class="progress-container">
              <div class="progress-bar"></div>
            </div>
        </div>
      }
    </div>
  `,
  styles: `
    .toast-container {
      z-index: 999999;
      pointer-events: none;
    }

    .premium-toast {
      pointer-events: auto;
      margin-bottom: 12px;
      min-width: 320px;
      max-width: 550px;
      border-radius: 6px;
      overflow: hidden;
      position: relative;
      background: white;
      transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }

    /* Phones: full-width banner along the top edge instead of a 320px
       overlay that overflows a 375px viewport (p-4 stays for desktop). */
    @media (max-width: 575.98px) {
      .toast-container {
        left: 0;
        right: 0;
        padding: 8px !important;
      }

      .premium-toast {
        min-width: 0;
        width: 100%;
        max-width: none;
      }
    }

    .toast-main-content {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      padding: 12px 18px;
      gap: 16px;
      margin-bottom: 5px;
    }

    .design-elements {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      pointer-events: none;
      z-index: 0;
    }

    .bg-icon-1 {
      position: absolute;
      left: -20px;
      top: -20px;
      font-size: 140px;
      width: 140px;
      height: 140px;
      opacity: 0.12;
      color: white;
      transform: rotate(-15deg);
    }

    .bg-icon-2 {
      position: absolute;
      right: 10%;
      bottom: -40px;
      font-size: 100px;
      width: 100px;
      height: 100px;
      opacity: 0.08;
      color: white;
      transform: rotate(20deg);
    }

    .icon-section {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .icon-section mat-icon {
      font-size: 28px;
      width: 28px;
      height: 28px;
      color: white;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
    }

    .text-section {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-height: 44px;
    }

    .toast-title {
      font-weight: 800;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
      color: rgba(255, 255, 255, 0.8);
    }

    .toast-message {
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
      color: white;
    }

    .action-section {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }

    .action-btn-text {
      font-size: 13px;
      font-weight: 800;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.9);
      margin-right: 8px;
      cursor: pointer;
      letter-spacing: 0.5px;
    }

    .close-btn {
      background: none;
      border: none;
      padding: 0;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .close-btn:hover {
      color: white;
      transform: scale(1.1);
    }

    .close-btn mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
    }

    /* Progress Bar */
    .progress-container {
      height: 3px;
      width: 100%;
      background: rgba(0, 0, 0, 0.08);
      position: absolute;
      bottom: 0;
      left: 0;
    }

    .progress-bar {
      height: 100%;
      background: rgba(255, 255, 255, 0.4);
      width: 100%;
      animation: toastProgress 5000ms linear forwards;
    }

    @keyframes toastProgress {
      from { width: 100%; }
      to { width: 0%; }
    }

    /* Variant Colors with Subtle Gradients and Shine */
    .bg-success {
      background: radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%),
                  linear-gradient(135deg, #4EC33D 0%, #3e9c30 100%) !important;
    }
    .bg-danger {
      background: radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%),
                  linear-gradient(135deg, #FC2E20 0%, #d12316 100%) !important;
    }
    .bg-info {
      background: radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%),
                  linear-gradient(135deg, #0070E0 0%, #005bb8 100%) !important;
    }
    .bg-warning {
      background: radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%),
                  linear-gradient(135deg, #FC8621 0%, #e07416 100%) !important;
    }

    /* Default / White Version */
    .premium-toast:not([class*="bg-"]) {
      background-color: white !important;
    }
    .premium-toast:not([class*="bg-"]) .toast-message {
      color: #2c3e50;
    }
    .premium-toast:not([class*="bg-"]) .icon-circle {
      background-color: transparent;
    }
    .premium-toast:not([class*="bg-"]) .icon-circle mat-icon {
      color: #2c3e50;
    }
    .premium-toast:not([class*="bg-"]) .close-btn,
    .premium-toast:not([class*="bg-"]) .action-btn-text {
      color: #2c3e50;
    }
    .premium-toast:not([class*="bg-"]) .bg-icon-1,
    .premium-toast:not([class*="bg-"]) .bg-icon-2 {
      color: #2c3e50;
      opacity: 0.05;
    }
    .premium-toast:not([class*="bg-"]) .progress-bar {
      background-color: #2c3e50;
      opacity: 0.2;
    }
  `,
})
export class ToastComponent {

  public toastService = inject(ToastService);
  toasts$ = this.toastService.toasts$;
}
